/**
 * What staff see when a screen throws, instead of React Router's bare default.
 *
 * 🔴 No route defined an `errorElement`, so any component that threw during render showed React
 * Router's own sentence, *"Unexpected Application Error"*, and logged nothing. On 15 Sep 2026
 * Kimberly hit exactly that uploading a document, and it cost an hour of elimination: the upload
 * endpoint was never reached, the handler was fully wrapped in try/catch, her case data was
 * ordinary, and the flow worked perfectly when reproduced locally. The cause was a stale browser
 * tab running the pre-deploy bundle against the new API. A hard refresh fixed it.
 *
 * So this says the two things that sentence did not: **what to try first**, because a refresh fixes
 * the most common cause, and **what actually happened**, so a report names the error rather than
 * describing the screen.
 *
 * It deliberately does not try to recover. A component that threw mid-render has unknown state, and
 * pretending otherwise is how a broken page becomes a wrong page.
 */
import React from 'react';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import { useRouteError } from 'react-router-dom';

function describe(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  // A thrown Response, which is what a loader failure looks like.
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status?: unknown }).status;
    const text = (error as { statusText?: unknown }).statusText;
    return `${String(status ?? '')} ${String(text ?? '')}`.trim();
  }
  return 'No details were given.';
}

const RouteError: React.FC = () => {
  const error = useRouteError();
  const detail = describe(error);

  // The console line is for whoever is looking at a screenshot of the browser console rather than
  // at this page. Router errors otherwise leave nothing behind.
  console.error('[route-error]', error);

  return (
    <Box sx={{ p: 3, maxWidth: 720 }}>
      <Alert severity="error">
        <AlertTitle>This screen could not be shown</AlertTitle>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Try a hard refresh first — <strong>Ctrl Shift R</strong>, or <strong>Cmd Shift R</strong> on
          a Mac. A tab left open from before an update is the most common cause and a refresh clears
          it.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          If it happens again, send this line with what you were doing:
        </Typography>
        <Typography
          variant="body2"
          component="pre"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            bgcolor: 'rgba(0,0,0,0.04)',
            p: 1,
            borderRadius: 1,
            mb: 2,
          }}
        >
          {detail}
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Reload the page
        </Button>
      </Alert>
    </Box>
  );
};

export default RouteError;
